import { useMemo, useState } from 'react';

import { InvoiceForCurrencyForLegalEntity } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import dynamic from 'next/dynamic';
import { parseAsArrayOf, parseAsInteger, useQueryState } from 'nuqs';

import { EmptyState } from '../../../../../shared/empty-state/empty-state.component';
import { usePaymentOptions } from '../../../hooks/use-payment-options';
import { OpenInvoiceFilters } from '../../../models/filters.model';
import { getInvoicesForLegalEntityAndCurrency } from '../../../utils/invoice.utils';
import { OpenInvoicesNotFoundIllustration } from '../../empty-states/open-invoices-not-found-illustration';

import { InvoiceCollapsible } from './invoice-collapsible';

const InvoicePaymentFlyout = dynamic(() => import('./payment-flyout/invoice-payment-flyout').then((mod) => mod.InvoicePaymentFlyout), { ssr: false });

const createCollapsibleId = (legalEntity: string, currency: string) => `${legalEntity}.${currency}`;

const getFirstCollapsibleId = (legalEntityInvoices: InvoiceForCurrencyForLegalEntity[]) =>
   legalEntityInvoices.length === 1 && legalEntityInvoices[0].currencyTransactions.length === 1
      ? createCollapsibleId(legalEntityInvoices[0].legalEntity, legalEntityInvoices[0].currencyTransactions[0].currency)
      : null;

type Props = {
   openInvoices: InvoiceForCurrencyForLegalEntity[];
   openInvoiceFilters: OpenInvoiceFilters;
   canPayOnline: boolean;
   canDeductCreditNotes: boolean;
};

export const OpenInvoiceList = ({ openInvoices, openInvoiceFilters, canPayOnline, canDeductCreditNotes }: Props) => {
   const translate = useTranslations();

   const [expandedId, setExpandedId] = useQueryState('expanded', {
      defaultValue: getFirstCollapsibleId(openInvoices) ?? '',
      clearOnDefault: false
   });
   const [lastExpandedId, setLastExpandedId] = useState(() => getFirstCollapsibleId(openInvoices));

   const [selectedInvoiceIds, setSelectedInvoiceIds] = useQueryState('selectedInvoices', parseAsArrayOf(parseAsInteger, ',').withDefault([]));

   const { paymentOptions, paymentProviderId, paymentProviderLoading, paymentProviderError, startPayment, clearPayment } = usePaymentOptions(
      openInvoices,
      selectedInvoiceIds
   );

   const handleExpandToggle = async (collapsibleId: string) => {
      if (collapsibleId === expandedId) {
         await setExpandedId('');
      } else {
         await setExpandedId(collapsibleId);
      }

      if (collapsibleId !== lastExpandedId) {
         await setSelectedInvoiceIds([]);
      }
      setLastExpandedId(collapsibleId);
   };

   const handleFlyoutClose = async () => {
      await clearPayment();
   };

   const selectedOpenInvoices = useMemo(() => {
      if (!paymentOptions) return undefined;
      return getInvoicesForLegalEntityAndCurrency(openInvoices, paymentOptions.legalEntity, paymentOptions.currency);
   }, [paymentOptions, openInvoices]);

   if (!openInvoices?.length) {
      return (
         <EmptyState
            label={translate('invoices.open-invoices.empty-state.label')}
            image={<OpenInvoicesNotFoundIllustration />}
            title={translate('invoices.open-invoices.empty-state.title')}
         />
      );
   }

   return (
      <>
         {openInvoices?.map((openInvoice) => (
            <Box key={openInvoice.legalEntity} direction="column">
               <Typography variant="body-highlight">{openInvoice.legalEntity}</Typography>
               {openInvoice.currencyTransactions.map((currencyTransaction) => {
                  if (currencyTransaction.transactions.length === 0) {
                     return null;
                  }

                  const collapsibleId = createCollapsibleId(openInvoice.legalEntity, currencyTransaction.currency);

                  return (
                     <InvoiceCollapsible
                        key={collapsibleId}
                        currency={currencyTransaction.currency}
                        openInvoices={currencyTransaction.transactions}
                        openInvoiceFilters={openInvoiceFilters}
                        selectedInvoiceIds={selectedInvoiceIds}
                        isExpanded={expandedId === collapsibleId}
                        canPayOnline={canPayOnline}
                        canDeductCreditNotes={canDeductCreditNotes}
                        onExpandToggle={() => handleExpandToggle(collapsibleId)}
                        onSelectedInvoiceIdsChange={(ids) => setSelectedInvoiceIds(ids)}
                        onPay={(type) =>
                           startPayment({
                              paymentType: type,
                              legalEntity: openInvoice.legalEntity,
                              currency: currencyTransaction.currency
                           })
                        }
                     />
                  );
               })}
            </Box>
         ))}

         {canPayOnline && (
            <InvoicePaymentFlyout
               open={!!paymentOptions && (!!paymentProviderId || paymentProviderLoading || paymentProviderError)}
               invoices={selectedOpenInvoices}
               selectedInvoiceIds={selectedInvoiceIds}
               paymentOptions={paymentOptions}
               paymentProviderId={paymentProviderId}
               paymentProviderError={paymentProviderError}
               canDeductCreditNotes={canDeductCreditNotes}
               onClose={handleFlyoutClose}
            />
         )}
      </>
   );
};
